import { mapValues, uniq, uniqWith, without } from 'es-toolkit';
import { createSelector } from '@reduxjs/toolkit';

import { Bundle, Truss } from '@paragon/contract-library';
import { distanceEquals, Point2D as Point2DGeometry, Polygon2D } from '@paragon/geometry-library';
import { BUNDLE_MAX_THICKNESS_MARKER, InstanceInfo, InstancePositions } from '@paragon/production-app-utilities';
import { notNullish } from '@paragon/utilities';

import { HeightCache } from '../ComponentGeometryCaches';
import { RootState } from '../store';
import { getDistanceFormatterSelector } from './getDistanceFormatterSelector';
import { getInstanceInfoByBundleGuid } from './getInstanceInfoByBundleGuid';
import { getInstancePositionsByBundleGuid } from './getInstancePositionsByBundleGuid';
import { getLoadedTrussesByAnalysisSetGuid } from './trusses';

interface BundleWarning {
  readonly longMessage: string;
  readonly shortMessage: string;
}

export const getWarningsByBundleGuid = createSelector(
  (state: RootState) => state.bundles,
  getInstanceInfoByBundleGuid,
  getInstancePositionsByBundleGuid,
  getLoadedTrussesByAnalysisSetGuid,
  getDistanceFormatterSelector,

  (
    bundles,
    instanceInfoByBundleGuid,
    instancePositionByBundleGuid,
    loadedTrussesByAnalysisSetGuid,
    distanceFormatter,
  ) =>
    mapValues(bundles, (bundle) => {
      const warnings: BundleWarning[] = [];

      const areOverlappingTrussesOrExceedsBundleThicknessWarnings =
        getAreOverlappingTrussesOrExceedsBundleThicknessWarnings(
          bundle,
          instanceInfoByBundleGuid[bundle.guid],
          instancePositionByBundleGuid[bundle.guid],
          distanceFormatter,
        );
      warnings.push(...areOverlappingTrussesOrExceedsBundleThicknessWarnings);

      const differingHeightsWarning = getDifferingHeightsWarning(
        bundle,
        instanceInfoByBundleGuid[bundle.guid],
        loadedTrussesByAnalysisSetGuid,
        distanceFormatter,
      );
      if (differingHeightsWarning !== null) warnings.push(differingHeightsWarning);

      return warnings;
    }),
);

function getAreOverlappingTrussesOrExceedsBundleThicknessWarnings(
  bundle: Bundle,
  instanceInfo: InstanceInfo,
  instancePositions: InstancePositions,
  distanceFormatter: (lengthInInches: number) => string,
) {
  const instancePolygons: Polygon2D[] = [];
  for (const [instanceGuid, position] of Object.entries(instancePositions)) {
    const instanceInfoForThis = instanceInfo[instanceGuid];
    if (!instanceInfoForThis) continue; // The truss may not be loaded yet
    const { width, thickness } = instanceInfoForThis;
    const points = [
      new Point2DGeometry(position.x, position.y),
      new Point2DGeometry(position.x + width, position.y),
      new Point2DGeometry(position.x + width, position.y + thickness),
      new Point2DGeometry(position.x, position.y + thickness),
    ];
    const instancePolygon = new Polygon2D(points);
    instancePolygons.push(instancePolygon);
  }

  let areOverlappingTrusses = false;
  for (const instancePolygon of instancePolygons) {
    const otherInstancePolygons = without(Object.values(instancePolygons), instancePolygon);
    // Include midpoints to identify cases where this truss's edges are directly on top of another truss
    const midpoints = instancePolygon.edges.map((edge) => edge.midpoint);
    const instancePolygonPoints = instancePolygon.vertices.concat(midpoints);
    const isOverlap = otherInstancePolygons.some((otherPolygon) =>
      instancePolygonPoints.some((point) => otherPolygon.containsPointOnInside(point)),
    );
    if (isOverlap) {
      areOverlappingTrusses = true;
      break;
    }
  }

  const exceedsBundleThickness = instancePolygons.some((polygon) =>
    polygon.vertices.some(
      (vertex) => !distanceEquals(BUNDLE_MAX_THICKNESS_MARKER, vertex.y) && vertex.y > BUNDLE_MAX_THICKNESS_MARKER,
    ),
  );

  const warnings: BundleWarning[] = [];
  if (areOverlappingTrusses) warnings.push({ shortMessage: 'Overlap', longMessage: 'Overlapping trusses' });
  if (exceedsBundleThickness)
    warnings.push({
      shortMessage: 'Oversized',
      longMessage: `Bundle thickness exceeds ${distanceFormatter(BUNDLE_MAX_THICKNESS_MARKER)}`,
    });
  return warnings;
}

function getDifferingHeightsWarning(
  bundle: Bundle,
  instanceInfo: InstanceInfo,
  loadedTrussesByAnalysisSetGuid: { [analysisSetGuid: string]: Truss },
  distanceFormatter: (lengthInInches: number) => string,
): BundleWarning | null {
  const analysisSetGuids = uniq(Object.values(instanceInfo).map((instanceInfo) => instanceInfo.analysisSetGuid));
  const analysisSetGuidsAndTrusses = analysisSetGuids
    .map((analysisSetGuid) =>
      analysisSetGuid in loadedTrussesByAnalysisSetGuid
        ? { analysisSetGuid, truss: loadedTrussesByAnalysisSetGuid[analysisSetGuid] }
        : null,
    )
    .filter(notNullish);
  const heights = analysisSetGuidsAndTrusses.map(({ analysisSetGuid, truss }) =>
    HeightCache.get(analysisSetGuid, truss),
  );
  const uniqueHeights = uniqWith(heights, distanceEquals);
  const differingHeights = uniqueHeights.length > 1;
  if (!differingHeights) return null;

  const minHeight = Math.min(...uniqueHeights);
  const maxHeight = Math.max(...uniqueHeights);
  return {
    shortMessage: 'Heights differ',
    longMessage: `Bundle contains trusses of differing heights (min: ${distanceFormatter(
      minHeight,
    )} max: ${distanceFormatter(maxHeight)} difference: ${distanceFormatter(maxHeight - minHeight)})`,
  };
}

// Clearspan does not want these warnings right now, but they may be useful for other manufacturers
// function getComponentsDifferFromProjectError(
//   bundle: Bundle,
//   analysisSetGuidsNotInProjectOrAtDifferentVersionByProductionGroupGuid: {
//     [productionGroupGuid: string]: readonly string[];
//   },
//   productionComponentInstancesByBundleGuid: { [bundleGuid: string]: readonly ProductionComponentInstance[] },
//   loadedTrussesByAnalysisSetGuid: { [analysisSetGuid: string]: Truss },
// ): BundleWarning | null {
//   const analysisSetGuidsNotInProjectForBundleProductionGroup =
//     analysisSetGuidsNotInProjectOrAtDifferentVersionByProductionGroupGuid[bundle.productionGroupGuid];
//   const analysisSetGuidsInBundle = uniq(
//     productionComponentInstancesByBundleGuid[bundle.guid].map((instance) => instance.analysisSetGuid),
//   );
//   const analysisSetGuidsInBundleThatDifferFromProject = intersection(
//     analysisSetGuidsInBundle,
//     analysisSetGuidsNotInProjectForBundleProductionGroup,
//   );
//   return analysisSetGuidsInBundleThatDifferFromProject.length === 0
//     ? null
//     : {
//         shortMessage: 'Differs from project',
//         longMessage: `These trusses do not exist in the project or exist at a different version: ${naturalSortByProperty(
//           analysisSetGuidsInBundleThatDifferFromProject
//             .map((analysisSetGuid) => loadedTrussesByAnalysisSetGuid[analysisSetGuid]?.name)
//             .filter(notNullish),
//           (trussName) => trussName,
//         ).join(', ')}`,
//       };
// }
